import { OsrGenerationOptions } from '../components/OsrGenerationOptions';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  CalendarDays,
  Gauge,
  Headphones,
  Layers3,
  ListOrdered,
  School,
  type LucideIcon,
} from 'lucide-react';
import { AnswerSheetScanPage } from './AnswerSheetScanPage';

import { useAuth } from '../context/AuthContext';
import { useProductStudyMode } from '../context/ProductStudyModeContext';
import {
  getLearningSequenceSettings,
  reviewService,
  updateLearningSequenceSettings,
  type LearningMode,
  type LearningSequenceSettings,
  type StreamSequenceLengthMap,
} from '../services/reviewService';
import {
  getAvailableStreamsForSubject,
  getDefaultStreamForSubject,
  getStreamConfig,
  SUBJECT_CODES,
  SUBJECT_CONFIG,
  type LearningEpisodeStream,
  type SubjectCode,
} from '../utils/subjectConfig';
import { accountUserProfileService } from '../services/accountUserProfileService';
import type { OffscreenInteractionMode, ProductStudyMode } from '../utils/productStudyModeStore';

const MODE_LENGTH_PRESETS: Record<LearningMode, number[]> = {
  weekday: [3, 5, 8],
  holiday: [8, 10, 12],
};

type SettingsSectionId = 'product' | 'calendar' | 'age' | 'sequence' | 'offscreen';

type SettingsNavItem = {
  id: SettingsSectionId;
  label: string;
  hint: string;
  icon: LucideIcon;
  reviewOnly?: boolean;
  offscreenOnly?: boolean;
};

const SETTINGS_NAV: SettingsNavItem[] = [
  {
    id: 'product',
    label: '产品模式',
    hint: '复习 / 离屏互斥',
    icon: Headphones,
  },
  {
    id: 'offscreen',
    label: '离屏参数',
    hint: '车道与 Glance',
    icon: Gauge,
    offscreenOnly: true,
  },
  {
    id: 'calendar',
    label: '课日与品题',
    hint: '课日模式 · 品题入列',
    icon: CalendarDays,
    reviewOnly: true,
  },
  {
    id: 'age',
    label: '内容超前年龄',
    hint: '生成内容年龄带',
    icon: Layers3,
  },
  {
    id: 'sequence',
    label: '学习序列长度',
    hint: '学科 Stream 日目标',
    icon: ListOrdered,
    reviewOnly: true,
  },
];

const PRODUCT_MODE_OPTIONS: Array<{
  key: ProductStudyMode;
  title: string;
  subtitle: string;
  description: string;
}> = [
  {
    key: 'review',
    title: '复习模式',
    subtitle: '刷卡 / 序列复习首页',
    description: '与离屏互斥。首页走素材流与今日复习任务。',
  },
  {
    key: 'offscreen',
    title: '离屏模式',
    subtitle: '听答 / OSR 调试面',
    description: '与复习互斥。用于离屏连播与 refine 后台效果调试。',
  },
];

const OFFSCREEN_LANE_OPTIONS: Array<{
  key: OffscreenInteractionMode;
  title: string;
  subtitle: string;
}> = [
  { key: 'broadcast', title: '单播', subtitle: '听题听答，少打断' },
  { key: 'dialogue', title: '离屏对话', subtitle: '围绕知识颗粒，一问一答' },
];

const OSR_MODEL_OPTIONS = [
  { value: '', label: '跟随 keliagent 默认模型', family: 'keliagent', modality: 'text', icon: null },
  { value: 'deepseek-v4-flash', label: 'DeepSeek V4 Flash', family: 'DeepSeek', modality: 'text', icon: 'deepseek-color.png' },
  { value: 'deepseek-v4-pro', label: 'DeepSeek V4 Pro', family: 'DeepSeek', modality: 'text', icon: 'deepseek-color.png' },
  { value: 'gpt-5.4', label: 'GPT 5.4', family: 'OpenAI', modality: 'text', icon: 'openai.png' },
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6', family: 'Anthropic', modality: 'text', icon: 'anthropic.png' },
  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', family: 'Google', modality: 'multimodal', icon: 'google-color.png' },
  { value: 'qwen3.7-plus', label: 'Qwen 3.7 Plus', family: 'Qwen', modality: 'multimodal', icon: 'qwen-color.png' },
  { value: 'qwen3.8-max', label: 'Qwen 3.8 Max', family: 'Qwen', modality: 'multimodal', icon: 'qwen-color.png' },
];

const LOBE_MODEL_ICON_BASE = 'https://registry.npmmirror.com/@lobehub/icons-static-png/latest/files/light/';

function SettingsPanel({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-surface p-5 sm:p-6">
      <div className="mb-5 border-b border-border pb-4">
        <h2 className="text-lg font-semibold text-ink">{title}</h2>
        {subtitle ? <p className="mt-1 text-sm text-muted">{subtitle}</p> : null}
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function ChoiceCard({
  selected,
  title,
  subtitle,
  description,
  icon,
  onClick,
}: {
  selected: boolean;
  title: string;
  subtitle?: string;
  description?: string;
  icon?: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-xl border px-4 py-3.5 text-left transition ${
        selected
          ? 'border-primary bg-[#EAF2FF] shadow-[inset_0_0_0_1px_rgba(47,111,237,0.18)]'
          : 'border-border bg-canvas hover:border-primary/35'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {icon}
            <span className={`text-sm font-semibold ${selected ? 'text-primary-strong' : 'text-ink'}`}>
              {title}
            </span>
          </div>
          {subtitle ? <p className="mt-1 text-xs text-muted">{subtitle}</p> : null}
          {description ? <p className="mt-1 text-xs leading-relaxed text-muted">{description}</p> : null}
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
            selected ? 'bg-primary text-white' : 'bg-surface text-muted'
          }`}
        >
          {selected ? '当前' : '切换'}
        </span>
      </div>
    </button>
  );
}

function buildStreamLengthState(
  weekdayDefault: number,
  holidayDefault: number,
  existing?: StreamSequenceLengthMap,
): StreamSequenceLengthMap {
  const next: StreamSequenceLengthMap = {};

  for (const subject of SUBJECT_CODES) {
    for (const stream of getAvailableStreamsForSubject(subject)) {
      const override = existing?.[subject]?.[stream];
      if (!next[subject]) {
        next[subject] = {};
      }
      next[subject]![stream] = {
        weekdaySequenceLength: override?.weekdaySequenceLength ?? weekdayDefault,
        holidaySequenceLength: override?.holidaySequenceLength ?? holidayDefault,
      };
    }
  }

  return next;
}

function readActiveLength(
  streamLengths: StreamSequenceLengthMap,
  subject: SubjectCode,
  stream: string,
  learningMode: LearningMode,
): number {
  const entry = streamLengths[subject]?.[stream];
  if (!entry) {
    return learningMode === 'holiday' ? 10 : 5;
  }
  return learningMode === 'holiday' ? entry.holidaySequenceLength : entry.weekdaySequenceLength;
}

function updateActiveLength(
  streamLengths: StreamSequenceLengthMap,
  subject: SubjectCode,
  stream: string,
  learningMode: LearningMode,
  value: number,
): StreamSequenceLengthMap {
  const current = streamLengths[subject]?.[stream] ?? {
    weekdaySequenceLength: 5,
    holidaySequenceLength: 10,
  };
  const nextSubjectMap = { ...(streamLengths[subject] || {}) };
  nextSubjectMap[stream] = learningMode === 'holiday'
    ? { ...current, holidaySequenceLength: value }
    : { ...current, weekdaySequenceLength: value };

  return {
    ...streamLengths,
    [subject]: nextSubjectMap,
  };
}

export function LearningSequencePage() {
  const { user } = useAuth();
  const {
    productMode,
    setProductMode,
    offscreenPrefs,
    setOffscreenGenerationGroups,
    setOffscreenInteractionMode,
    setOffscreenAllowGlance,
    setOffscreenModel,
  } = useProductStudyMode();
  const isOffscreenSettings = productMode === 'offscreen';
  const [settings, setSettings] = useState<LearningSequenceSettings | null>(null);
  const [streamSequenceLengths, setStreamSequenceLengths] = useState<StreamSequenceLengthMap>({});
  const [currentSubject, setCurrentSubject] = useState<SubjectCode>('math');
  const [currentStream, setCurrentStream] = useState<LearningEpisodeStream | null>(null);
  const [learnerAgeYears, setLearnerAgeYears] = useState<number | null>(null);
  const [activeSection, setActiveSection] = useState<SettingsSectionId>('product');

  useEffect(() => {
    if (!user?.id) return;
    void (async () => {
      await reviewService.syncLearningEpisodeStreamRegistry({ forceRefresh: true });
      let nextSettings = getLearningSequenceSettings(user.id);
      try {
        const remote = await accountUserProfileService.getLearningSequence(user.id);
        const patch: Partial<LearningSequenceSettings> = {};
        if (typeof remote.includeIpttqInReviewSequence === 'boolean') {
          patch.includeIpttqInReviewSequence = remote.includeIpttqInReviewSequence;
        }
        if (remote.contentAgeAheadYears != null && Number.isFinite(Number(remote.contentAgeAheadYears))) {
          patch.contentAgeAheadYears = Math.min(10, Math.max(0, Math.round(Number(remote.contentAgeAheadYears))));
        }
        if (Object.keys(patch).length > 0) {
          nextSettings = updateLearningSequenceSettings(user.id, patch);
        }
      } catch {
        // 本地设置兜底
      }
      try {
        const bundle = await accountUserProfileService.getProfileBundle(user.id);
        const age = bundle.profile?.derivedAgeYears;
        setLearnerAgeYears(
          age != null && Number.isFinite(age) && age >= 0 ? age : null,
        );
      } catch {
        setLearnerAgeYears(null);
      }
      setSettings(nextSettings);
      setStreamSequenceLengths(buildStreamLengthState(
        nextSettings.weekdaySequenceLength,
        nextSettings.holidaySequenceLength,
        nextSettings.streamSequenceLengths,
      ));
    })();
  }, [user?.id]);

  const contentAgeCoverageLabel = useMemo(() => {
    const ahead = settings?.contentAgeAheadYears ?? 1;
    const baseAge = learnerAgeYears != null ? learnerAgeYears : 8;
    return `${baseAge + ahead - 1}~${baseAge + ahead + 2}`;
  }, [learnerAgeYears, settings?.contentAgeAheadYears]);

  const subjectStreams = useMemo(
    () => getAvailableStreamsForSubject(currentSubject),
    [currentSubject],
  );

  const visibleNav = useMemo(
    () => SETTINGS_NAV.filter((item) => {
      if (item.reviewOnly && isOffscreenSettings) return false;
      if (item.offscreenOnly && !isOffscreenSettings) return false;
      return true;
    }),
    [isOffscreenSettings],
  );

  useEffect(() => {
    const defaultStream = getDefaultStreamForSubject(currentSubject);
    setCurrentStream((previous) => {
      if (previous && subjectStreams.includes(previous)) {
        return previous;
      }
      return defaultStream;
    });
  }, [currentSubject, subjectStreams]);

  useEffect(() => {
    if (!visibleNav.some((item) => item.id === activeSection)) {
      setActiveSection(visibleNav[0]?.id ?? 'product');
    }
  }, [activeSection, visibleNav]);

  if (!user?.id || !settings) return null;

  const learningMode = settings.learningMode;
  const lengthPresets = MODE_LENGTH_PRESETS[learningMode];
  const subjectColor = SUBJECT_CONFIG[currentSubject].color;
  const selectedStream = currentStream && subjectStreams.includes(currentStream) ? currentStream : null;
  const selectedLength = selectedStream
    ? readActiveLength(streamSequenceLengths, currentSubject, selectedStream, learningMode)
    : null;

  const persistSettings = (patch: Partial<LearningSequenceSettings>) => {
    const next = updateLearningSequenceSettings(user.id, patch);
    setSettings(next);
  };

  const applyStreamLength = (stream: LearningEpisodeStream, value: number) => {
    const nextLengths = updateActiveLength(streamSequenceLengths, currentSubject, stream, learningMode, value);
    setStreamSequenceLengths(nextLengths);
    persistSettings({ streamSequenceLengths: nextLengths });
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-ink">学习设置</h1>
        <p className="mt-1 text-sm text-muted">
          左侧选分类，右侧改参数。当前产品面：
          <span className="ml-1 font-medium text-primary-strong">
            {isOffscreenSettings ? '离屏模式' : '复习模式'}
          </span>
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
        <nav
          aria-label="学习设置分类"
          className="h-fit rounded-2xl border border-border bg-surface p-2 lg:sticky lg:top-4"
        >
          <div className="mb-1 hidden px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted lg:block">
            分类
          </div>
          <div className="flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible">
            {visibleNav.map((item) => {
              const selected = item.id === activeSection;
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveSection(item.id)}
                  className={`flex min-w-[9.5rem] items-start gap-2.5 rounded-xl px-3 py-2.5 text-left transition lg:min-w-0 ${
                    selected
                      ? 'bg-[#EAF2FF] text-primary-strong'
                      : 'text-ink hover:bg-canvas'
                  }`}
                >
                  <Icon
                    size={16}
                    className={`mt-0.5 shrink-0 ${selected ? 'text-primary-strong' : 'text-muted'}`}
                    strokeWidth={2.2}
                  />
                  <span className="min-w-0">
                    <span className={`block text-sm font-semibold ${selected ? 'text-primary-strong' : 'text-ink'}`}>
                      {item.label}
                    </span>
                    <span className="mt-0.5 block text-[11px] text-muted">{item.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        <div className="min-w-0">
          {activeSection === 'product' ? (
            <SettingsPanel
              title="产品模式"
              subtitle="复习与离屏互斥；切换后首页产品面随之变化。"
            >
              <div className="space-y-2">
                {PRODUCT_MODE_OPTIONS.map((option) => {
                  const selected = option.key === productMode;
                  return (
                    <ChoiceCard
                      key={option.key}
                      selected={selected}
                      title={option.title}
                      subtitle={option.subtitle}
                      description={option.description}
                      icon={option.key === 'review'
                        ? <School size={15} className={selected ? 'text-primary-strong' : 'text-muted'} />
                        : <Headphones size={15} className={selected ? 'text-primary-strong' : 'text-muted'} />}
                      onClick={() => {
                        setProductMode(option.key);
                      }}
                    />
                  );
                })}
              </div>
            </SettingsPanel>
          ) : null}

          {activeSection === 'offscreen' ? (
            <SettingsPanel
              title="离屏参数"
              subtitle="与 Mobile2 / HOSN 对齐的车道与 Glance 偏好。"
            >
              <div className="space-y-2">
                {OFFSCREEN_LANE_OPTIONS.map((option) => {
                  const selected = option.key === offscreenPrefs.interactionMode;
                  return (
                    <ChoiceCard
                      key={option.key}
                      selected={selected}
                      title={option.title}
                      subtitle={option.subtitle}
                      onClick={() => {
                        if (option.key === offscreenPrefs.interactionMode) return;
                        setOffscreenInteractionMode(option.key);
                      }}
                    />
                  );
                })}
              </div>
              <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-canvas px-4 py-3.5">
                <input
                  type="checkbox"
                  className="mt-1 accent-primary"
                  checked={offscreenPrefs.allowGlance}
                  onChange={(e) => setOffscreenAllowGlance(e.target.checked)}
                />
                <span>
                  <span className="block text-sm font-medium text-ink">允许 Glance</span>
                  <span className="mt-0.5 block text-xs leading-relaxed text-muted">
                    离屏时允许闪示关键词 / 封面位；关闭则尽量纯听。
                  </span>
                </span>
              </label>
              <div className="space-y-3 rounded-xl border border-border bg-canvas px-4 py-3.5">
                <p className="text-sm font-medium text-ink">默认生成体量</p>
                <OsrGenerationOptions value={offscreenPrefs.generationGroups} onChange={setOffscreenGenerationGroups} />
              </div>
              <label className="block rounded-xl border border-border bg-canvas px-4 py-3.5">
                <span className="block text-sm font-medium text-ink">口播卡模型</span>
                <span className="mt-0.5 block text-xs leading-relaxed text-muted">
                  选择本次通过 keliagent 生成口播卡所使用的模型；默认跟随服务端配置。
                </span>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {OSR_MODEL_OPTIONS.map((option) => {
                    const selected = offscreenPrefs.osrModel === option.value;
                    return (
                      <button
                        key={option.value || 'default'}
                        type="button"
                        className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm ${selected ? 'border-primary bg-[#EAF2FF]' : 'border-border bg-surface hover:border-primary/35'}`}
                        onClick={() => setOffscreenModel(option.value)}
                        aria-pressed={selected}
                      >
                        {option.icon ? <img src={`${LOBE_MODEL_ICON_BASE}${option.icon}`} alt="" width={18} height={18} className="object-contain" /> : <span className="flex h-[18px] w-[18px] items-center justify-center text-[10px] font-bold text-primary">K</span>}
                        <span className="min-w-0 flex-1 truncate font-medium text-ink">{option.label}</span>
                        {option.modality === 'multimodal' ? <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] text-violet-700">多模态</span> : null}
                      </button>
                    );
                  })}
                </div>
              </label>
            </SettingsPanel>
          ) : null}

          {activeSection === 'calendar' ? (
            <SettingsPanel
              title="课日与品题"
              subtitle="仅复习模式生效：课日 / 节假日节奏，以及品题是否进入复习列。"
            >
              <div className="grid gap-2 sm:grid-cols-2">
                {([
                  { key: 'weekday' as const, title: '课日模式', subtitle: '日常较短序列' },
                  { key: 'holiday' as const, title: '节假日模式', subtitle: '加长复习批次' },
                ]).map((option) => {
                  const selected = settings.learningMode === option.key;
                  return (
                    <ChoiceCard
                      key={option.key}
                      selected={selected}
                      title={option.title}
                      subtitle={option.subtitle}
                      onClick={() => {
                        if (selected) return;
                        persistSettings({ learningMode: option.key });
                      }}
                    />
                  );
                })}
              </div>
              <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-canvas px-4 py-3.5">
                <input
                  type="checkbox"
                  className="mt-1 accent-primary"
                  checked={Boolean(settings.includeIpttqInReviewSequence)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    persistSettings({ includeIpttqInReviewSequence: checked });
                    if (user?.id) {
                      void accountUserProfileService.patchLearningSequence(user.id, {
                        includeIpttqInReviewSequence: checked,
                      }).catch(() => {
                        // 本地设置已写入；服务端同步失败时不影响本页
                      });
                    }
                  }}
                />
                <span>
                  <span className="block text-sm font-medium text-ink">品题加入复习列</span>
                  <span className="mt-0.5 block text-xs leading-relaxed text-muted">
                    打开后，复习序列仍先命中原始知识颗粒，再按约 5:1 替换为品题浓缩诱导题库（或抽查原题）。关闭则完全走原算法。
                  </span>
                </span>
              </label>
            </SettingsPanel>
          ) : null}

          {activeSection === 'age' ? (
            <SettingsPanel
              title="内容超前年龄"
              subtitle="适合学习内容超前的复习与离屏情形；两种产品模式共用。"
            >
              <p className="text-sm text-muted">
                当前生成式内容覆盖年龄范围是：
                <span className="ml-1 text-base font-bold text-primary">
                  {contentAgeCoverageLabel}
                </span>
              </p>
              <div className="rounded-xl border border-border bg-canvas px-4 py-4">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-medium text-ink">超前 +{settings.contentAgeAheadYears ?? 1}</span>
                  <span className="text-xs text-muted">0（贴近实龄）～ 10（大幅超前）</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={10}
                  step={1}
                  value={settings.contentAgeAheadYears ?? 1}
                  onChange={(e) => {
                    const value = Math.min(10, Math.max(0, Math.round(Number(e.target.value))));
                    persistSettings({ contentAgeAheadYears: value });
                    if (user?.id) {
                      void accountUserProfileService.patchLearningSequence(user.id, {
                        contentAgeAheadYears: value,
                      }).catch(() => {
                        // 本地已写入
                      });
                    }
                  }}
                  className="mt-4 w-full accent-primary"
                />
              </div>
            </SettingsPanel>
          ) : null}

          {activeSection === 'sequence' ? (
            <SettingsPanel
              title="学习序列长度"
              subtitle="切换学科后选择 Stream，设定该流的每日复习目标；完成对应张数即算当日任务完成。"
            >
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {SUBJECT_CODES.map((subject) => {
                  const selected = subject === currentSubject;
                  return (
                    <button
                      key={subject}
                      type="button"
                      onClick={() => {
                        setCurrentSubject(subject);
                        setCurrentStream(getDefaultStreamForSubject(subject));
                      }}
                      className={`rounded-xl border px-3 py-3 text-sm font-semibold transition ${
                        selected ? 'border-2' : 'border-border bg-canvas text-ink'
                      }`}
                      style={selected
                        ? {
                          borderColor: SUBJECT_CONFIG[subject].color,
                          color: SUBJECT_CONFIG[subject].color,
                          backgroundColor: `${SUBJECT_CONFIG[subject].color}12`,
                        }
                        : undefined}
                    >
                      {SUBJECT_CONFIG[subject].label}
                    </button>
                  );
                })}
              </div>

              <div className="-mx-1 overflow-x-auto px-1 pb-1">
                <div className="flex w-max gap-2">
                  {subjectStreams.map((stream) => {
                    const streamConfig = getStreamConfig(stream);
                    const isActive = stream === selectedStream;
                    const streamLength = readActiveLength(
                      streamSequenceLengths,
                      currentSubject,
                      stream,
                      learningMode,
                    );

                    return (
                      <button
                        key={stream}
                        type="button"
                        onClick={() => setCurrentStream(stream)}
                        className={`flex shrink-0 flex-col items-center justify-center rounded-xl border px-5 py-3 text-center transition ${
                          isActive ? 'border-2' : 'border-border bg-canvas'
                        }`}
                        style={isActive
                          ? { borderColor: subjectColor, backgroundColor: `${subjectColor}10` }
                          : undefined}
                      >
                        <div
                          className={`whitespace-nowrap text-sm font-semibold ${isActive ? '' : 'text-ink'}`}
                          style={isActive ? { color: subjectColor } : undefined}
                        >
                          {streamConfig.label}
                        </div>
                        <div
                          className={`mt-1 whitespace-nowrap text-xs font-bold ${isActive ? '' : 'text-muted'}`}
                          style={isActive ? { color: subjectColor } : undefined}
                        >
                          {streamLength} 张 / 批
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {selectedStream && selectedLength !== null ? (
                <div className="space-y-3 rounded-xl border border-border bg-canvas p-4">
                  <div className="font-medium text-ink">
                    {getStreamConfig(selectedStream).label} · 序列长度
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => applyStreamLength(selectedStream, Math.max(1, selectedLength - 1))}
                      className="h-10 w-10 rounded-full border border-border bg-surface text-lg font-semibold text-ink"
                    >
                      -
                    </button>
                    <div
                      className="flex-1 rounded-xl border px-4 py-3 text-center"
                      style={{ borderColor: `${subjectColor}55`, backgroundColor: `${subjectColor}12` }}
                    >
                      <div className="text-2xl font-bold" style={{ color: subjectColor }}>{selectedLength}</div>
                      <div className="text-xs text-muted">张 / 批</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => applyStreamLength(selectedStream, Math.min(12, selectedLength + 1))}
                      className="h-10 w-10 rounded-full border border-border bg-surface text-lg font-semibold text-ink"
                    >
                      +
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {lengthPresets.map((preset) => (
                      <button
                        key={`${currentSubject}-${selectedStream}-${preset}`}
                        type="button"
                        onClick={() => applyStreamLength(selectedStream, preset)}
                        className={`rounded-full border px-3 py-1 text-sm ${
                          selectedLength === preset ? 'font-semibold' : 'border-border bg-surface'
                        }`}
                        style={selectedLength === preset
                          ? {
                            borderColor: subjectColor,
                            color: subjectColor,
                            backgroundColor: `${subjectColor}12`,
                          }
                          : undefined}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted">点击上方 Stream 标签，设置该流的序列长度。</p>
              )}
            </SettingsPanel>
          ) : null}
        </div>
      </div>
    </div>
  );
}


export function LearningSettingsPage() {
  return <LearningSequencePage />;
}

export function LearningFootprintPage() {
  const { user } = useAuth();
  const [entries, setEntries] = useState<Array<{ id: string; label: string; at: string }>>([]);

  useEffect(() => {
    if (!user?.id) return;
    void (async () => {
      try {
        const snapshot = await reviewService.loadFamilyFootprint(user.id);
        setEntries(
          (snapshot?.rows || []).slice(0, 50).map((entry) => ({
            id: entry.id,
            label: entry.particleTitle || entry.particleId,
            at: entry.completedAt,
          })),
        );
      } catch {
        setEntries([]);
      }
    })();
  }, [user?.id]);

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-ink">学习足迹</h1>
      <ul className="divide-y divide-border rounded-xl border border-border bg-surface">
        {entries.map((entry) => (
          <li key={entry.id} className="px-4 py-3 text-sm">
            <div className="font-medium text-ink">{entry.label}</div>
            {entry.at ? <div className="text-muted">{new Date(entry.at).toLocaleString()}</div> : null}
          </li>
        ))}
        {!entries.length ? <li className="px-4 py-6 text-sm text-muted">暂无足迹记录</li> : null}
      </ul>
    </div>
  );
}

export function BookmarksPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<Array<{ particleId: string; title: string }>>([]);

  useEffect(() => {
    if (!user?.id) return;
    void (async () => {
      try {
        const snapshot = await reviewService.loadRazBookmarks(user.id);
        setItems(
          (snapshot?.rows || []).map((entry) => ({
            particleId: entry.particleId,
            title: entry.particleTitle || entry.title || entry.particleId,
          })),
        );
      } catch {
        setItems([]);
      }
    })();
  }, [user?.id]);

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-ink">书签</h1>
      <ul className="divide-y divide-border rounded-xl border border-border bg-surface">
        {items.map((item) => (
          <li key={item.particleId} className="px-4 py-3 text-sm">
            <a href={`/knowledge/${item.particleId}`} className="text-primary hover:underline">{item.title}</a>
          </li>
        ))}
        {!items.length ? <li className="px-4 py-6 text-sm text-muted">暂无书签</li> : null}
      </ul>
    </div>
  );
}

export function AnnotationsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<Array<{ particleId: string; note: string }>>([]);

  useEffect(() => {
    if (!user?.id) return;
    void (async () => {
      try {
        const snapshot = await reviewService.loadDifficultyAnnotations(user.id);
        setItems(
          (snapshot?.rows || []).map((entry) => ({
            particleId: entry.particleId || entry.eventId,
            note: entry.actionLabel || entry.actionIntent,
          })),
        );
      } catch {
        setItems([]);
      }
    })();
  }, [user?.id]);

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-ink">批注</h1>
      <ul className="divide-y divide-border rounded-xl border border-border bg-surface">
        {items.map((item) => (
          <li key={item.particleId} className="px-4 py-3 text-sm">
            <div className="font-medium text-ink">{item.note}</div>
            <a href={`/knowledge/${item.particleId}`} className="text-primary hover:underline">{item.particleId}</a>
          </li>
        ))}
        {!items.length ? <li className="px-4 py-6 text-sm text-muted">暂无批注</li> : null}
      </ul>
    </div>
  );
}

export function FeedbackPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<Array<{ particleId: string; feedback: string }>>([]);

  useEffect(() => {
    if (!user?.id) return;
    void (async () => {
      try {
        const snapshot = await reviewService.loadQualityFeedback(user.id);
        setItems(
          (snapshot?.rows || []).map((entry) => ({
            particleId: entry.particleId || entry.eventId,
            feedback: entry.qualityReasonLabel || entry.qualityReasonCodes?.join(', ') || '—',
          })),
        );
      } catch {
        setItems([]);
      }
    })();
  }, [user?.id]);

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-ink">卡反馈</h1>
      <ul className="divide-y divide-border rounded-xl border border-border bg-surface">
        {items.map((item) => (
          <li key={item.particleId} className="px-4 py-3 text-sm">
            <div>{item.feedback}</div>
            <a href={`/knowledge/${item.particleId}`} className="text-primary hover:underline">{item.particleId}</a>
          </li>
        ))}
        {!items.length ? <li className="px-4 py-6 text-sm text-muted">暂无反馈</li> : null}
      </ul>
    </div>
  );
}

export function ProfileModelsPage() {
  const { user } = useAuth();
  const [model, setModel] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    void (async () => {
      const { userPreferencesService } = await import('../services/userPreferencesService');
      const pref = await userPreferencesService.getMaterialExtractionPreference(user.id);
      setModel(pref.defaultModel);
    })();
  }, [user?.id]);

  async function handleSelect(nextModel: string) {
    if (!user?.id) return;
    const {
      isMaterialExtractionModelSelectable,
    } = await import('../utils/materialExtractionModels');
    if (!isMaterialExtractionModelSelectable(nextModel as import('../utils/materialExtractionModels').MaterialExtractionModelValue)) {
      return;
    }

    const { userPreferencesService } = await import('../services/userPreferencesService');
    const preference = await userPreferencesService.updateDefaultMaterialExtractionModel(user.id, nextModel);
    setModel(preference.defaultModel);
    setMessage('已保存默认提取模型');
  }

  return (
    <div className="mx-auto max-w-md space-y-4">
      <h1 className="text-2xl font-semibold text-ink">模型配置</h1>
      <p className="text-sm text-muted">当前仅开放 Qwen VL Max，其余模型后续逐步开放。</p>
      <ModelOptionsList selectedModel={model} onSelect={(nextModel) => void handleSelect(nextModel)} />
      {message ? <p className="text-sm text-muted">{message}</p> : null}
    </div>
  );
}

function ModelOptionsList({
  selectedModel,
  onSelect,
}: {
  selectedModel: string;
  onSelect: (model: string) => void;
}) {
  const [options, setOptions] = useState<Array<{
    value: string;
    label: string;
    subtitle: string;
    recommended?: boolean;
    selectable?: boolean;
  }>>([]);

  useEffect(() => {
    void import('../utils/materialExtractionModels').then(({ MATERIAL_EXTRACTION_MODEL_OPTIONS }) => {
      setOptions(MATERIAL_EXTRACTION_MODEL_OPTIONS);
    });
  }, []);

  return (
    <div className="space-y-3">
      {options.map((option) => {
        const selected = option.value === selectedModel;
        const selectable = option.selectable !== false;
        return (
          <button
            key={option.value}
            type="button"
            disabled={!selectable}
            onClick={() => onSelect(option.value)}
            className={[
              'w-full rounded-xl border px-4 py-3 text-left transition',
              selected ? 'border-primary bg-surface-accent' : 'border-border bg-surface',
              selectable ? 'hover:bg-canvas' : 'cursor-not-allowed opacity-60',
            ].join(' ')}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-ink">{option.label}</span>
                  {option.recommended ? <span className="rounded-full bg-primary px-2 py-0.5 text-xs text-white">默认推荐</span> : null}
                  {!selectable ? <span className="rounded-full bg-canvas px-2 py-0.5 text-xs text-muted">即将推出</span> : null}
                </div>
                <p className="mt-1 text-sm text-muted">{option.subtitle}</p>
              </div>
              {selected ? <span className="text-sm font-semibold text-primary">当前</span> : null}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export function ScanUploadPage() {
  return <AnswerSheetScanPage />;
}
